<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :disabled="isLocked"
    :close-on-click="!keepOpen"
    max-width="290"
    offset-y
  >
    <template v-slot:activator="{}">
      <v-text-field
        ref="field"
        v-bind="fieldAttrs"
        v-on="fieldListeners"
        :value="text"
        :error-messages="error"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholder"
        :color="color"
        clearable
        autocomplete="off"
        inputmode="numeric"
        maxlength="10"
        @input="onInput"
        @keypress="onKeypress"
        @keydown="onKeydown"
        @focus="focused = true"
        @blur="onBlur"
        @click:clear="onClear"
      >
        <template v-for="name in forwardedSlots" v-slot:[name]="slotData">
          <slot :name="name" v-bind="slotData"></slot>
        </template>
        <template v-slot:[iconSlot]>
          <v-icon
            :color="iconColor"
            :disabled="isLocked"
            @mousedown.prevent
            @click.stop="menu = !menu"
          >{{ icon }}</v-icon>
        </template>
      </v-text-field>
    </template>
    <div>
      <v-date-picker
        :value="value"
        :color="color"
        :min="min"
        :max="max"
        :allowed-dates="allowedDates"
        v-bind="pickerProps"
        @input="onPick"
      ></v-date-picker>
      <slot name="footer"></slot>
    </div>
  </v-menu>
</template>

<script>
// Date input that can be typed (DD/MM/YYYY) or picked from the calendar.
// v-model is an ISO string (YYYY-MM-DD) or null, same as v-date-picker.
export default {
  name: "DateField",
  inheritAttrs: false,
  props: {
    value: { type: String, default: null },
    min: { type: String, default: undefined },
    max: { type: String, default: undefined },
    allowedDates: { type: Function, default: undefined },
    color: { type: String, default: "#13736f" },
    placeholder: { type: String, default: "DD/MM/YYYY" },
    icon: { type: String, default: "mdi-calendar" },
    iconColor: { type: String, default: "#64748b" },
    // "append" or "prepend-inner"
    iconPosition: { type: String, default: "append" },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    // stop outside clicks from closing the calendar (e.g. while a dialog is over it)
    keepOpen: { type: Boolean, default: false },
    // extra props for v-date-picker (events, event-color, first-day-of-week...)
    pickerProps: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      menu: false,
      text: "",
      error: "",
      focused: false,
    };
  },
  computed: {
    isLocked() {
      return this.disabled || this.readonly;
    },
    iconSlot() {
      return this.iconPosition === "prepend-inner" ? "prepend-inner" : "append";
    },
    forwardedSlots() {
      const own = ["footer", "append", "prepend-inner"];
      return Object.keys(this.$scopedSlots).filter((n) => !own.includes(n));
    },
    fieldAttrs() {
      const attrs = { ...this.$attrs };
      // Let the validation message show even when the parent hides details.
      if (this.error) attrs["hide-details"] = "auto";
      return attrs;
    },
    fieldListeners() {
      const l = { ...this.$listeners };
      ["input", "change", "focus", "blur", "keypress", "keydown", "click:clear"].forEach(
        (e) => delete l[e]
      );
      return l;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v !== this.parse(this.text)) {
          this.text = this.display(v);
          this.error = "";
        }
      },
    },
    menu(open) {
      if (!open && !this.focused) this.finalize();
    },
  },
  methods: {
    display(iso) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
      return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
    },
    // "d/m/yyyy" -> "yyyy-mm-dd", or null when it is not a real calendar date
    parse(str) {
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((str || "").trim());
      if (!m) return null;
      const d = +m[1];
      const mo = +m[2];
      const y = +m[3];
      const dt = new Date(y, mo - 1, d);
      if (y < 1900 || dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
        return null;
      }
      return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    },
    check(iso) {
      if (!iso) return "Enter a valid date (DD/MM/YYYY)";
      if (this.min && this.min.length === 10 && iso < this.min) {
        return `Date cannot be before ${this.display(this.min)}`;
      }
      if (this.max && this.max.length === 10 && iso > this.max) {
        return `Date cannot be after ${this.display(this.max)}`;
      }
      if (this.allowedDates && !this.allowedDates(iso)) return "This date is not available";
      return "";
    },
    commit(iso) {
      if (iso !== this.value) {
        this.$emit("input", iso);
        this.$emit("change", iso);
      }
    },
    normalize(raw) {
      const iso = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(raw);
      if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
      let v = raw.replace(/[^\d/.-]/g, "").replace(/[.-]/g, "/").replace(/\/{2,}/g, "/");
      v = v.replace(/^\//, "");
      // typing forward: add the separator after day and month
      if (v.length > this.text.length && /^(\d{2}|\d{1,2}\/\d{2})$/.test(v)) v += "/";
      return v.slice(0, 10);
    },
    onInput(raw) {
      const v = this.normalize(raw == null ? "" : String(raw));
      this.text = v;
      this.$nextTick(() => {
        const input = this.$refs.field && this.$refs.field.$refs.input;
        if (input && input.value !== this.text) input.value = this.text;
      });
      if (!v) {
        this.error = "";
        this.commit(null);
        return;
      }
      const iso = this.parse(v);
      if (!iso) {
        this.error = "";
        return;
      }
      this.error = this.check(iso);
      if (!this.error) this.commit(iso);
    },
    onKeypress(e) {
      if (e.ctrlKey || e.metaKey) return;
      if (e.key && e.key.length === 1 && !/[\d/.-]/.test(e.key)) e.preventDefault();
    },
    onKeydown(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        this.finalize();
        this.menu = false;
      } else if (e.key === "Escape") {
        this.menu = false;
      }
    },
    onBlur() {
      this.focused = false;
      if (!this.menu) this.finalize();
    },
    finalize() {
      const t = this.text.trim();
      if (!t) {
        this.error = "";
        this.commit(null);
        return;
      }
      const iso = this.parse(t);
      this.error = this.check(iso);
      if (this.error) {
        // keep the field and the model in agreement: an invalid entry is no date
        this.commit(null);
      } else {
        this.text = this.display(iso);
        this.commit(iso);
      }
    },
    onPick(iso) {
      this.error = "";
      this.text = this.display(iso);
      this.menu = false;
      this.commit(iso);
    },
    onClear() {
      this.text = "";
      this.error = "";
      this.commit(null);
      this.$emit("click:clear");
    },
  },
};
</script>
